import LegalPageLayout from "@/components/LegalPageLayout";
import SEO from "@/components/SEO";

export default function TermsOfEngagement() {
  return (
    <>
      <SEO
        title="Terms of Engagement | YourSolicitor.co.uk"
        description="Read the full Terms of Engagement for BlueLion Claims, including our fee structure, No Win No Fee conditions, cancellation rights and your responsibilities as a client."
        canonical="/terms-of-engagement"
        noIndex={false}
      />
      <LegalPageLayout title="Terms of Engagement" lastUpdated="January 2025">
      <p className="legal-intro">
        Please read these Terms of Engagement carefully. By providing your signature (including electronically), you agree to be bound by these terms.
      </p>

      <h2>1. Definitions and Interpretation</h2>
      <p><strong>1.1</strong> "Agreement" means the contractual relationship between you and us which you enter by providing your signature, including where you provide your signature electronically.</p>
      <p><strong>1.2</strong> "BlueLion Claims" means BlueLion Law Limited. Any reference to 'we', 'us' or 'our' shall be taken to be a reference to BlueLion Law Limited. "Cancellation Period" means 14 days following the date on which you provide your signature.</p>
      <p><strong>1.3</strong> "Claim(s)" means a complaint or potential complaint against the Lender relating to (i) failure to disclose commission charges; (ii) failure to undertake sufficient affordability checks; (iii) mis-sale of ancillary products; and/or (iv) any other unfair lending practices identified.</p>
      <p><strong>1.4</strong> "Claims Services" means the work we undertake to assess, prepare, submit and negotiate the Claim(s) with the Lender.</p>
      <p><strong>1.5</strong> "Compensation" means any sums offered, paid or given in respect of a settlement, goodwill gesture, policy refund or rebate or any other payment associated with the Claim(s) including interest payments. For the avoidance of doubt, Compensation also includes any sums used to reduce outstanding balances/debt.</p>
      <p><strong>1.6</strong> "Credit Product" means a financial product allowing you to borrow money from a lender.</p>
      <p><strong>1.7</strong> "DBA Fee" means the amount you will have to pay us for our services as set out in Clause 5.</p>
      <p><strong>1.8</strong> "FOS" means the Financial Ombudsman Service, which can review the Lender's decision about your Claim(s).</p>
      <p><strong>1.9</strong> "FOS Representative Fees" means £250, which is reduced to £75 if it upholds your complaint.</p>
      <p><strong>1.10</strong> "Lender" means the party against which you pursue the Claim / your opponent.</p>

      <h2>2. Our Services</h2>
      <p><strong>2.1</strong> Upon receiving your instructions, you authorise us to undertake a Credit Record Request / subject access request to identify the lenders with which you held car finance.</p>
      <p><strong>2.2</strong> If we identify any other lenders you may have a Claim against, you authorise us to pursue those Claims on your behalf.</p>
      <p><strong>2.3</strong> Once we identify your car finance lender and substantiate your Claim, we will submit a Claim to your Lender.</p>
      <p><strong>2.4</strong> Where your Lender does not provide a satisfactory outcome, we will assess whether to submit your Claim to FOS and/or process your Claim through litigation where we consider it has a reasonable prospect of success.</p>
      <p><strong>2.5</strong> We will keep you updated throughout the Claim(s) and promptly notify you of the outcome of each Claim, including any offers of Compensation.</p>

      <h2>3. Our Responsibilities</h2>
      <ul>
        <li>Always act in your best interests, subject to our regulatory duties.</li>
        <li>Provide you with a reasonable standard of service in line with your agreement with us.</li>
        <li>Explain to you the risks and benefits of taking legal action.</li>
        <li>Give you our best advice about whether to accept any offer of settlement.</li>
        <li>Keep you updated on all developments throughout your Claim(s).</li>
        <li>Progress your Claim(s) as quickly and efficiently as we can.</li>
      </ul>
      <p><strong>3.11</strong> Our total liability to you shall in no circumstances exceed £3,000,000, being the minimum level of professional indemnity insurance we are required by the SRA to maintain.</p>

      <h2>4. Your Responsibilities</h2>
      <p>By entering into this Agreement, you acknowledge that you could pursue the Claim(s) yourself, either directly with your Lender and through FOS, or through the FCA's proposed industry-wide compensation scheme free of charge.</p>
      <p><strong>You must:</strong></p>
      <ul>
        <li>Cooperate with us and provide clear and timely instructions.</li>
        <li>Notify us of any change of contact details as soon as practicable.</li>
        <li>Reply to requests for information or documents within 10 business days.</li>
        <li>Inform us promptly of any matters affecting the Claim(s), including direct contact from the Lender.</li>
        <li>Not settle the Claim(s) without our consent and without first cancelling this Agreement.</li>
        <li>Confirm within 21 days whether you wish to accept any offer of settlement.</li>
        <li>Give us the exclusive right to deal with the Claim(s).</li>
        <li>Observe good faith in all your dealings with us, disclosing all relevant information.</li>
      </ul>

      <h2>5. Compensation, Fees and Charges under a DBA</h2>
      <p><strong>5.2</strong> If we are not successful in obtaining an offer of Compensation then you owe us nothing, providing that you comply with your responsibilities in Clause 4.</p>
      <p><strong>5.3</strong> Under the terms of the DBA, if we are successful, our DBA Fee will be calculated as follows:</p>
      <div className="legal-table-wrap">
        <table className="legal-table">
          <thead>
            <tr>
              <th>Compensation</th>
              <th>DBA Fee % (excl. VAT)</th>
              <th>Max Fee (excl. VAT)</th>
              <th>Max Fee (incl. VAT)</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>£1 – £1,499</td><td>30%</td><td>£420</td><td>£504</td></tr>
            <tr><td>£1,500 – £9,999</td><td>28%</td><td>£2,500</td><td>£3,000</td></tr>
            <tr><td>£10,000 – £24,999</td><td>25%</td><td>£5,000</td><td>£6,000</td></tr>
            <tr><td>£25,000 – £49,999</td><td>20%</td><td>£7,500</td><td>£9,000</td></tr>
            <tr><td>£50,000 or more</td><td>15%</td><td>£10,000</td><td>£12,000</td></tr>
          </tbody>
        </table>
      </div>
      <p><strong>5.5</strong> We provide an estimate of our DBA Fee based on different Compensation values:</p>
      <div className="legal-table-wrap">
        <table className="legal-table">
          <thead>
            <tr><th>Compensation</th><th>DBA Fee (excl. VAT)</th><th>VAT</th></tr>
          </thead>
          <tbody>
            <tr><td>£1,000</td><td>£300</td><td>£60</td></tr>
            <tr><td>£3,000</td><td>£840</td><td>£168</td></tr>
            <tr><td>£5,000</td><td>£1,400</td><td>£280</td></tr>
          </tbody>
        </table>
      </div>

      <h2>6. Compensation, Fees and Charges under a CFA</h2>
      <p><strong>6.1</strong> If we are not successful in obtaining an offer of Compensation then you owe us nothing, providing that you comply with your responsibilities above.</p>
      <p><strong>6.2</strong> If we are successful, our London-based hourly rates (including VAT) are:</p>
      <div className="legal-table-wrap">
        <table className="legal-table">
          <thead>
            <tr><th>Grade of Fee Earner</th><th>Hourly Rate</th><th>Including VAT</th></tr>
          </thead>
          <tbody>
            <tr><td>Partner</td><td>£650</td><td>£780</td></tr>
            <tr><td>Senior Partner</td><td>£780</td><td>£936</td></tr>
            <tr><td>Legal Associate</td><td>£256 – £385</td><td>£310 – £460</td></tr>
            <tr><td>Assistants and Paralegals</td><td>£150 – £215</td><td>£180 – £258</td></tr>
            <tr><td>Consultant</td><td>£300</td><td>£360</td></tr>
          </tbody>
        </table>
      </div>

      <h2>9. Cancellation and Assignment</h2>
      <p><strong>9.1</strong> Under the Consumer Contracts Regulations 2013, you have the right to cancel this contract within 14 days ("cooling off period") without giving any reason and at no charge to you. You must notify us of your intention to cancel by using the cancellation notice in your Client Pack and sending it by post or email to:</p>
      <div className="legal-contact-box">
        <div className="legal-contact-item">BlueLion Claims, The Centro Buildings, 20–23 Mandela Street, London NW1 0DU</div>
        <div className="legal-contact-item"><a href="mailto:negar.yazdani@bluelionlaw.co.uk">negar.yazdani@bluelionlaw.co.uk</a></div>
      </div>

      <h2>13. Anti-Money Laundering Requirements</h2>
      <p><strong>13.1</strong> Under The Money Laundering Regulations 2017 and The Proceeds of Crime Act 2002, all law firm employees and partners are required to report any knowledge or suspicion of money laundering or client involvement in the proceeds of crime to the National Crime Agency (NCA).</p>
      <p><strong>13.2</strong> Our policy is not to accept cash payments in excess of £1,500 without prior written agreement.</p>

      <h2>14. Client Care and Complaint Handling</h2>
      <p><strong>14.1</strong> We are committed to providing high quality legal advice and client care. If you are unhappy about any aspect of service, please contact Rebecca Riches, or alternatively Negar Yazdani (Managing Partner) at <a href="mailto:negar.yazdani@bluelionlaw.co.uk">negar.yazdani@bluelionlaw.co.uk</a>.</p>
      <p><strong>14.3</strong> Our complaints procedure is available upon request. We have eight weeks to consider your complaint. If unresolved, you may complain to the Legal Ombudsman (0300 5550333, <a href="mailto:enquiries@legalombudsman.org.uk">enquiries@legalombudsman.org.uk</a>, PO Box 6167, Slough SL1 0EH).</p>
      <p><strong>14.4</strong> Our professional indemnity insurance is with Bridgehaven Specialty UK Limited (policy no P10809425).</p>

      <h2>21. Governing Law and Jurisdiction</h2>
      <p>These Terms of Engagement and any dispute arising out of or in connection with them will be governed by and construed in accordance with the laws of England and Wales. The Courts of England and Wales will have exclusive jurisdiction.</p>

      <h2>Contact Us</h2>
      <div className="legal-contact-box">
        <div className="legal-contact-item"><strong>Email:</strong> <a href="mailto:contactus@bluelionclaims.co.uk">contactus@bluelionclaims.co.uk</a></div>
        <div className="legal-contact-item"><strong>Phone:</strong> 0208 153 0033</div>
      </div>
    </LegalPageLayout>
    </>
  );
}
